import { AxiosRequestConfig } from 'axios';
import Cookies from 'universal-cookie';

import { globalError } from '../../shared/notification/actions';
import { appConfig } from '../../config';
import { initTogglz, MULTI_LANGUAGE_SHOP, useFeature } from '../../shared/utils/togglz';
import { AppDispatch, GetAppState } from '../index';
import {
    AuthenticationAction,
    KC_FIND_USERS_FAILURE,
    KC_FIND_USERS_REQUEST,
    KC_FIND_USERS_SUCCESS,
    KC_IMPERSONATE,
    KeyCloakFindUsersRequest,
    SET_SPOC,
    SET_SPOC_FINISHED,
    SOFT_RESET
} from './types';
import { AuthenticationState, SpocState } from './state';
import { useDispatch, useSelector } from 'react-redux';
import { useCallback, useEffect } from 'react';
import { AppState } from '../appState';
import { useLocation } from 'react-router';
import { http } from '../../api/axios';
import { browserClient } from '../../react-query';
import { NavigateFunction, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthentication } from '../../app-store/authentication/useAuthentication';

const SPOC_COOKIE_NAME = 'spoc';
const TUTORIAL_COOKIE_NAME = 'tutorial';

// FIXME It seems this file has some separation of concerns issues. It is used for some initialization in index.js, we should have a look at this.

let cookie: Cookies | null = null; // FIXME should be initialized properly

const setSpocAction = (spoc: SpocState): AuthenticationAction => {
    return {
        type: SET_SPOC,
        payload: spoc
    };
};

export const setSpocFinished = (): AuthenticationAction => {
    return {
        type: SET_SPOC_FINISHED
    };
};

const softResetAction = (): AuthenticationAction => {
    return {
        type: SOFT_RESET
    };
};

const keyCloakFindUsersRequest = (searchString?: string): KeyCloakFindUsersRequest => {
    return {
        type: KC_FIND_USERS_REQUEST,
        payload: searchString
    };
};

const keyCloakFindUsersSuccess = (users: any): AuthenticationAction => {
    return {
        type: KC_FIND_USERS_SUCCESS,
        payload: users
    };
};

const keyCloakFindUsersFailure = (): AuthenticationAction => {
    return {
        type: KC_FIND_USERS_FAILURE
    };
};

const keyCloakImpersonate = (user: any): AuthenticationAction => {
    return {
        type: KC_IMPERSONATE,
        payload: user
    };
};

export function setAuthenticatedSpoc(spoc: SpocState, pathname: string, navigate: NavigateFunction) {
    saveSpocCookie(spoc);

    return (dispatch: AppDispatch, getState: GetAppState) => {
        const spocChangeIsLocked = getState().authentication.lockSpocChange;
        if (spocChangeIsLocked) {
            return;
        }
        dispatch(setSpocAction(spoc));
        navigate({
            pathname,
            search: new URLSearchParams({ spocId: spoc.id }).toString()
        });
        dispatch(softResetApp());
        browserClient.clear();
    };
}

function softResetApp() {
    return (dispatch: AppDispatch) => {
        dispatch(softResetAction());
        http.get(appConfig().endpointTogglz).then(togglz => {
            initTogglz(togglz.data);
            dispatch(setSpocFinished());
        });
    };
}

export function useSwitchSpoc() {
    const dispatch = useDispatch();
    const allSpocs = useSelector((state: AppState) => state.authentication.spocs);
    const spocChangeIsLocked = useSelector((state: AppState) => state.authentication.lockSpocChange);
    const { search } = useLocation();
    const navigate = useNavigate();

    return useCallback(
        (spocId: string) => {
            const desiredSpoc = findSpoc(allSpocs, spocId);
            if (desiredSpoc === null) {
                // the user is not allowed to switch to the desired spoc -> redirect to the root page
                navigate('/', { replace: true });
                return;
            }

            saveSpocCookie(desiredSpoc);

            if (spocChangeIsLocked) {
                return;
            }

            const searchParams = new URLSearchParams(search);
            const spocIdParam = searchParams.get('spocId');
            if (spocIdParam && spocIdParam !== spocId) {
                searchParams.set('spocId', spocId);
            }

            dispatch(setSpocAction(desiredSpoc));
            navigate({ search: searchParams.toString() });
            dispatch(softResetApp());
        },
        [allSpocs, dispatch, navigate, search, spocChangeIsLocked]
    );
}

export function useAutomaticSpocSwitch() {
    const switchSpoc = useSwitchSpoc();
    const [searchParams] = useSearchParams();

    const currentSpoc = useSelector((state: AppState) => state.authentication.spoc);

    const spocIdParam = searchParams.get('spocId');

    useEffect(() => {
        if (spocIdParam && spocIdParam !== currentSpoc.id) {
            switchSpoc(spocIdParam);
        }
    }, [currentSpoc.id, spocIdParam, switchSpoc]);
}

export function useSetAuthenticatedSpocById() {
    const isMultiLanguageShopEnabled = useFeature(MULTI_LANGUAGE_SHOP);
    const authentication = useAuthentication();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { pathname } = useLocation();

    return (newSpocId: string) => {
        const currentSpocId = authentication.spoc.id;
        const nextSearchParams = new URLSearchParams(searchParams);
        nextSearchParams.set('spocId', currentSpocId);
        navigate(
            { pathname: isMultiLanguageShopEnabled ? '/shop' : '/catalogue', search: nextSearchParams.toString() },
            { replace: true }
        );

        const desiredSpoc = findSpoc(authentication.spocs, newSpocId);
        if (desiredSpoc !== null) {
            dispatch(setAuthenticatedSpoc(desiredSpoc, pathname, navigate));
        }
    };
}

function findSpoc(spocs, spocId) {
    const desiredSpoc = spocs.reduce((desiredSpoc, spoc) => {
        if (spoc.id === spocId) {
            desiredSpoc = desiredSpoc.concat(spoc);
        } else {
            const site = spoc.sites.filter(site => site.id === spocId);
            if (site.length) {
                desiredSpoc = desiredSpoc.concat(site);
            }
        }
        return desiredSpoc;
    }, []);
    if (desiredSpoc.length) {
        return desiredSpoc[0];
    }
    return null;
}

export function logout(kc) {
    deleteTutorialCookie();
    deleteSpocCookie();
    deleteLocalStorageToken();
    // eslint-disable-next-line no-restricted-globals
    kc.logout({ redirectUri: location.origin });
}

export function loadSpocCookie() {
    return cookie?.get(SPOC_COOKIE_NAME);
}

export function initCookie() {
    if (!cookie) {
        cookie = new Cookies();
    }
}

function saveSpocCookie(spoc) {
    if (spoc.sites && spoc.sites.length > 0) {
        spoc.sites = [];
    }
    cookie?.set(SPOC_COOKIE_NAME, spoc, { path: '/' });
}

function deleteSpocCookie() {
    cookie?.remove(SPOC_COOKIE_NAME, { path: '/' });
}

function deleteLocalStorageToken() {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
}

type TutorialCookie = {
    readonly showIntroVideo: boolean;
    readonly showReserveVideo: boolean;
};

export function loadTutorialCookie(): TutorialCookie {
    const cookieValue = cookie?.get(TUTORIAL_COOKIE_NAME);

    if (typeof cookieValue === 'object') {
        return {
            showIntroVideo: Boolean(cookieValue.showIntroVideo),
            showReserveVideo: Boolean(cookieValue.showReserveVideo)
        };
    }

    return { showIntroVideo: true, showReserveVideo: true };
}

export function saveTutorialCookie(tutorialCookie: TutorialCookie) {
    cookie?.set(TUTORIAL_COOKIE_NAME, tutorialCookie, { path: '/' });
}

export function deleteTutorialCookie() {
    cookie?.remove(TUTORIAL_COOKIE_NAME, { path: '/' });
}

export function isCatalogueManagementAdmin(auth: AuthenticationState) {
    return auth && auth.kc && auth.kc.hasRealmRole('catalogue_management_admin');
}

export function isAllowedToPublishToNetwork(auth) {
    return auth && auth.kc && auth.kc.hasRealmRole('lc_publish_for_network');
}

export function hasLCManageRole(auth) {
    return auth && auth.kc && auth.kc.hasRealmRole('lc_manage');
}

export function isImpersonationAllowed(keycloak) {
    return (
        keycloak &&
        keycloak.hasResourceRole('impersonation', 'realm-management') &&
        keycloak.hasResourceRole('view-users', 'realm-management')
    );
}

export function findUsers(searchString?: string) {
    return (dispatch: AppDispatch, getState: GetAppState) => {
        dispatch(keyCloakFindUsersRequest(searchString));

        const kc = getState().authentication.kc;
        const endpoint = `${kc.authServerUrl}/admin/realms/${kc.realm}/users`;

        const config = createKcConfig(kc);
        config.params = {
            search: searchString,
            first: 0,
            max: 50
        };

        http.get(endpoint, config)
            .then(response => {
                dispatch(keyCloakFindUsersSuccess(response.data));
            })
            .catch(error => {
                dispatch(keyCloakFindUsersFailure());
                globalError(error);
            });
    };
}

export function impersonateUser(user) {
    return (dispatch: AppDispatch, getState: GetAppState) => {
        dispatch(keyCloakImpersonate(user));

        const kc = getState().authentication.kc;
        const endpoint = `${kc.authServerUrl}/admin/realms/${kc.realm}/users/${user.id}/impersonation`;
        const config = createKcConfig(kc);

        http.post(endpoint, '', config)
            .then(() => {
                deleteSpocCookie();
                window.location.href = '/';
            })
            .catch(error => globalError(error));
    };
}

// FIXME name is rather create axios config
function createKcConfig(kc): AxiosRequestConfig {
    return {
        // @ts-ignore
        external: true, // FIXME: does not exist in axios config
        withCredentials: true,
        headers: {
            Authorization: `Bearer ${kc.token}`
        }
    };
}
